import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Field, FieldError, FieldGroup, FieldLabel } from '~/shadcn/ui/field'
import {
  Form,
  FormDescription,
  FormField,
  FormItem,
} from '~/shadcn/ui/form/form'
import { Input } from '~/shadcn/ui/input'
import { cn } from '~/shadcn/lib/utils'
import { Spinner } from '~/shadcn/ui/spinner'
import { useLocalNotifications } from '~/@mid_ui/local-notifications/useLocalNotifications'
import { useChangePassword } from './useChangePassword'
import {
  changePasswordSchema,
  type ChangePasswordSchema,
} from './changePasswordSchema'
import { FormError } from '~/@mid_ui-form/form-error/FormError'
import { Button } from '~/shadcn/ui/button/button'

type FormModel = ChangePasswordSchema

function ChangePasswordForm() {
  const form = useForm<FormModel>({
    defaultValues: { oldPassword: '', newPassword: '', confirmPassword: '' },
    resolver: zodResolver(changePasswordSchema),
  })
  const { setNotification } = useLocalNotifications()
  const {
    changePassword: changePasswordMutation,
    isRequesting,
    error,
  } = useChangePassword()

  function handleChangePassword(values: FormModel) {
    changePasswordMutation(values, {
      onSuccess: () => {
        form.reset()
        setNotification('success', 'Heslo bolo úspešne zmenené')
      },
      onError: () => {
        form.reset()
      },
    })
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleChangePassword)}
        className="w-full flex flex-col gap-10 flex-grow pb-[60px]pt-10 lg:pb-[60px]">
        <FieldGroup>
          <div className="flex flex-col gap-2 lg:flex-row">
            <FormField
              control={form.control}
              name="oldPassword"
              render={({ fieldState, field }) => (
                <FormItem className="w-full lg:w-[300px]">
                  <Field>
                    <FieldLabel htmlFor="oldPassword">Súčasné heslo</FieldLabel>
                    <Input
                      id="oldPassword"
                      type="password"
                      autoComplete="current-password"
                      className={cn(
                        'w-full',
                        fieldState.error &&
                          'border-destructive aria-invalid:border-destructive'
                      )}
                      aria-invalid={!!fieldState.error}
                      {...field}
                    />
                  </Field>
                  {fieldState?.error?.message && (
                    <FieldError>{fieldState.error.message}</FieldError>
                  )}
                </FormItem>
              )}
            />
          </div>

          <div className="flex flex-col gap-4 lg:flex-row">
            <FormField
              control={form.control}
              name="newPassword"
              render={({ fieldState, field }) => (
                <FormItem className="w-full lg:w-[300px]">
                  <Field>
                    <FieldLabel htmlFor="newPassword">Heslo</FieldLabel>
                    <Input
                      id="newPassword"
                      type="password"
                      autoComplete="new-password"
                      className={cn(
                        'w-full',
                        fieldState.error &&
                          'border-destructive aria-invalid:border-destructive'
                      )}
                      aria-invalid={!!fieldState.error}
                      {...field}
                    />
                  </Field>
                  {fieldState.error ? (
                    <FieldError>
                      Najmenej 8 znakov, veľké a malé písmená a číslo.
                    </FieldError>
                  ) : (
                    <FormDescription>
                      Najmenej 8 znakov, veľké a malé písmená a číslo.
                    </FormDescription>
                  )}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ fieldState, field }) => (
                <FormItem className="w-full lg:w-[300px]">
                  <Field>
                    <FieldLabel htmlFor="confirmPassword">
                      Kontrola hesla
                    </FieldLabel>
                    <Input
                      id="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      className={cn(
                        'w-full',
                        fieldState.error &&
                          'border-destructive aria-invalid:border-destructive'
                      )}
                      aria-invalid={!!fieldState.error}
                      {...field}
                    />
                  </Field>
                  {fieldState?.error?.message && (
                    <FieldError>{fieldState.error.message}</FieldError>
                  )}
                </FormItem>
              )}
            />
          </div>
          {error && <FormError error={error} className="text-left" />}
        </FieldGroup>

        <div className="block w-full">
          <Button
            variant="outline"
            disabled={!!Object.keys(form.formState.errors).length}
            type="submit">
            Zmeniť heslo{` `}
            {isRequesting && <Spinner className="size-4" />}
          </Button>
        </div>
      </form>
    </Form>
  )
}

export { ChangePasswordForm }
